import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, ReplaySubject, Subject, catchError, exhaustMap, filter, map, switchMap, take } from 'rxjs';
import { CanViewPlatformNamespaceRequest, ViewAccessApiService } from '@vendasta/platform-users';
import { debounceTime, tap } from 'rxjs/operators';
import { AtlasConfigService } from './config.service';
import { PamDialogService } from '../../src/lib/pam-dialog/pam-dialog.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class PlatformAccessService {
  private readonly configService = inject(AtlasConfigService);
  private readonly viewAccessService = inject(ViewAccessApiService);
  private readonly pamDialogService = inject(PamDialogService);
  private readonly router = inject(Router);

  private readonly checkAccess$$ = new Subject<void>();
  private readonly accessCheckResult$$ = new ReplaySubject<boolean | null>(1);
  private _checkInProgress = false;
  get checkInProgress(): boolean { return this._checkInProgress; }

  /** Emits true when access is confirmed, false when denied or dialog is dismissed without granting. */
  readonly accessCheckResult$: Observable<boolean> = this.accessCheckResult$$.pipe(
    filter((v): v is boolean => v !== null),
  );

  constructor() {
    this.checkAccess$$.pipe(
      debounceTime(200),
      exhaustMap(() =>
        this.configService.config$.pipe(
          take(1),
          exhaustMap((cfg) => {
            this._checkInProgress = true;
            return this.viewAccessService
              .canViewPlatformNamespace(
                new CanViewPlatformNamespaceRequest({
                  partnerId: cfg.partnerId,
                  accountGroupId: cfg.accountId,
                  groupId: cfg.groupPath,
                  entitlementId: cfg.entitlementId,
                }),
              )
              .pipe(
                tap(() => (this._checkInProgress = false)),
                map((resp) => ({ ...resp, ...cfg })),
                catchError(() => {
                  this._checkInProgress = false;
                  this.accessCheckResult$$.next(false);
                  return EMPTY;
                }),
              );
          }),
          switchMap((cfgAndResp) => {
            if (cfgAndResp.canViewPlatformNamespace) {
              this.accessCheckResult$$.next(true);
              return EMPTY;
            } else if (cfgAndResp.canRequestEntitlement) {
              const ref = this.pamDialogService.openDialog(cfgAndResp.partnerId, cfgAndResp.accountId, cfgAndResp.defaultPamReason);
              return ref.afterClosed().pipe(
                tap((granted) => {
                  if (!granted) this.accessCheckResult$$.next(false);
                }),
                filter((granted) => !!granted),
                tap(() => this.accessCheckResult$$.next(true)),
              );
            } else {
              this.router.navigate([cfgAndResp.accessDeniedPath ?? '/']);
              this.accessCheckResult$$.next(false);
              return EMPTY;
            }
          }),
        ),
      ),
      takeUntilDestroyed(),
    ).subscribe();
  }

  public checkPlatformAccess() {
    this.accessCheckResult$$.next(null);
    this.checkAccess$$.next();
  }
}
